// How Confirm Pick-Up has gone lately — the evidence for the range setting.
//
// Every successful slide writes a `booking_events` row (`confirm_pick_up` on
// the initial leg, `therapist_pickup_confirmed` on a Return), every refused one
// writes `confirm_pick_up_refused` since 0293, and a superadmin override writes
// its own action. Before 0293 only overrides were visible, so the failure rate
// could not be seen at all. booking_events is readable by admin, executive and
// superadmin (0005); this reads `meta` only — no client field exists there.

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export const PICKUP_ACTIONS = [
  "confirm_pick_up",
  "therapist_pickup_confirmed",
  "confirm_pick_up_refused",
  "admin_force_confirm_pick_up",
  "admin_force_confirm_return_pick_up",
] as const;

export interface PickupStats {
  confirmed: number;
  refused: number;
  overridden: number;
  refusedByCode: Record<
    "RIDER_TOO_FAR" | "THERAPIST_LOCATION_STALE" | "RIDER_LOCATION_STALE",
    number
  >;
  /** Median distance of successful confirmations, metres; null with none measured. */
  medianConfirmedM: number | null;
}

type EventMeta = { action?: unknown; code?: unknown; distance_m?: unknown };

/** Pure, so it is unit-tested without a database. */
export function summarizePickupEvents(metas: readonly EventMeta[]): PickupStats {
  const stats: PickupStats = {
    confirmed: 0,
    refused: 0,
    overridden: 0,
    refusedByCode: { RIDER_TOO_FAR: 0, THERAPIST_LOCATION_STALE: 0, RIDER_LOCATION_STALE: 0 },
    medianConfirmedM: null,
  };
  const distances: number[] = [];
  for (const m of metas) {
    switch (m.action) {
      case "confirm_pick_up":
      case "therapist_pickup_confirmed": {
        stats.confirmed += 1;
        const d = Number(m.distance_m);
        if (m.distance_m !== undefined && m.distance_m !== null && Number.isFinite(d))
          distances.push(d);
        break;
      }
      case "confirm_pick_up_refused": {
        stats.refused += 1;
        if (
          m.code === "RIDER_TOO_FAR" ||
          m.code === "THERAPIST_LOCATION_STALE" ||
          m.code === "RIDER_LOCATION_STALE"
        ) {
          stats.refusedByCode[m.code] += 1;
        }
        break;
      }
      case "admin_force_confirm_pick_up":
      case "admin_force_confirm_return_pick_up":
        stats.overridden += 1;
        break;
    }
  }
  if (distances.length > 0) {
    const sorted = [...distances].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    stats.medianConfirmedM =
      sorted.length % 2 === 1 ? sorted[mid]! : (sorted[mid - 1]! + sorted[mid]!) / 2;
  }
  return stats;
}

export const PICKUP_STATS_DAYS = 7;

export function usePickupStats() {
  return useQuery({
    queryKey: ["pickup-stats", PICKUP_STATS_DAYS],
    queryFn: async () => {
      const since = new Date(Date.now() - PICKUP_STATS_DAYS * 86_400_000).toISOString();
      const { data, error } = await supabase
        .from("booking_events")
        .select("meta")
        .gte("created_at", since)
        .in("meta->>action", [...PICKUP_ACTIONS])
        .limit(5_000);
      if (error) throw new Error(error.message);
      return summarizePickupEvents((data ?? []).map((r) => (r.meta ?? {}) as EventMeta));
    },
    staleTime: 60_000,
  });
}
